import {
  baseURL,
  getCamiCode,
  GetCamiForeverCodesListWithPagination,
  GetFreeCamiCodesListWithPagination,
  getCamiCodesListWithPaginationByFilter,
} from "../Repository/Api";
import {
  buildWatchUrl,
  resolveLatestVideoIdFromCamiRow,
  rowBelongsToUser,
  videosFromCamiListRow,
} from "../Repository/CamiCodeVideosApi";
import { SPONSOR_COACH_ID_STORAGE_KEY } from "../Context/AuthUserContext";

const KNOWN_APP_PATHS = new Set([
  "",
  "account",
  "my-camicodes",
  "your-camicodes",
  "created-qrcode",
  "received-qrcode",
  "my-match-list",
  "games-videos",
  "comment-upload-success",
  "my-payment-methods",
  "my-profile",
  "my-transaction",
  "qrcard",
  "qrcard-received",
  "free-qr",
  "forever-qr",
  "login",
  "register",
  "forgot-password",
  "privacy-policy",
  "qr-steps",
  "create-qr",
  "scan-qr",
  "upload-video",
  "watch",
  "previous-videos",
  "order-complete",
  "reset-password",
  "coachconnect-forever",
]);

export function applyDurationLocalStorage(duration) {
  if (duration == "29" || duration == 29) {
    localStorage.setItem("RT", "P1");
  } else if (
    duration == "180" ||
    duration == 180 ||
    duration == "900" ||
    duration == 900 ||
    duration == "1200" ||
    duration == 1200
  ) {
    localStorage.setItem("RT", "P2");
  }
}

function normalizeEmail(value) {
  return value ? String(value).trim().toLowerCase() : "";
}

function normalizeUniqueId(value) {
  return String(value ?? "").replace(/ /g, "").trim();
}

function resolveUserFilterId(profile) {
  const userId = Number(profile?.id ?? profile?.userId);
  if (Number.isFinite(userId) && userId > 0) {
    return userId;
  }
  const coachId = Number(profile?.coachId);
  return Number.isFinite(coachId) && coachId > 0 ? coachId : 0;
}

function resolveListFilterId(profile) {
  try {
    const fromStorage = localStorage.getItem(SPONSOR_COACH_ID_STORAGE_KEY);
    if (fromStorage != null && fromStorage !== "") {
      const coachId = Number(fromStorage);
      if (Number.isFinite(coachId) && coachId > 0) {
        return coachId;
      }
    }
  } catch {
    /* ignore */
  }
  return resolveUserFilterId(profile);
}

function extractListRows(data) {
  if (!data) {
    return [];
  }
  const raw = data.list ?? data.items ?? data.data ?? data;
  return Array.isArray(raw) ? raw : [];
}

function rowMatchesScannedCami(row, camiCode, password) {
  const normalizedCode = String(camiCode || "").trim();
  const normalizedPass = normalizeUniqueId(password);
  const rowCodes = [row?.camiCode, row?.id, row?.camiCodeId]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);
  const rowPasses = [row?.uniqueId, row?.password, row?.uniqueID]
    .map((value) => normalizeUniqueId(value))
    .filter(Boolean);

  const codeMatches = rowCodes.some((code) => code === normalizedCode);
  if (!codeMatches) {
    return false;
  }
  if (!normalizedPass) {
    return true;
  }
  return rowPasses.length === 0 || rowPasses.some((pass) => pass === normalizedPass);
}

function rowOwnedByProfile(row, profile, ownerFilterId) {
  if (rowBelongsToUser(row, profile)) {
    return true;
  }
  const profileUserId = Number(profile?.id ?? profile?.userId);
  if (
    Number.isFinite(profileUserId) &&
    profileUserId > 0 &&
    ownerFilterId === profileUserId
  ) {
    return true;
  }
  return false;
}

async function fetchOwnedCamiRows(profile) {
  const rows = [];
  const ownerFilterId = resolveUserFilterId(profile);
  const listFilterId = resolveListFilterId(profile);
  const fetchJobs = [];

  if (ownerFilterId > 0) {
    fetchJobs.push(getCamiCodesListWithPaginationByFilter(1, 100, ownerFilterId, ""));
    fetchJobs.push(GetFreeCamiCodesListWithPagination(1, 100, ownerFilterId, ""));
  }
  if (listFilterId > 0 && listFilterId !== ownerFilterId) {
    fetchJobs.push(getCamiCodesListWithPaginationByFilter(1, 100, listFilterId, ""));
  }
  if (profile?.email) {
    fetchJobs.push(
      GetCamiForeverCodesListWithPagination(1, 100, ownerFilterId || 0, "", profile.email)
    );
  }

  const results = await Promise.allSettled(fetchJobs);
  results.forEach((result) => {
    if (result.status === "fulfilled") {
      rows.push(...extractListRows(result.value));
    }
  });

  return { rows, ownerFilterId };
}

async function camiCodeOwnedViaGetCamiCode(camiCode, password, authUser, profile) {
  if (!camiCode || !password) {
    return false;
  }
  try {
    const result = await getCamiCode(camiCode, password);
    if (!result || result.status === "error") {
      return false;
    }
    return camiCodeBelongsToUser(result, authUser, profile);
  } catch {
    return false;
  }
}

function buildEffectiveAuthUser(authUser, profile) {
  if (authUser?.email) {
    return authUser;
  }
  if (profile?.email) {
    return { email: profile.email };
  }
  return null;
}

/** True when the logged-in user owns this QR code (by profile id/email or auth email). */
export function camiCodeBelongsToUser(verifyResult, authUser, profile = null) {
  if (!verifyResult) return false;
  if (profile && rowBelongsToUser(verifyResult, profile)) return true;

  const effectiveAuthUser = buildEffectiveAuthUser(authUser, profile);
  if (!effectiveAuthUser?.email) return false;

  const userEmail = normalizeEmail(effectiveAuthUser.email);
  const ownerEmails = [
    verifyResult.email,
    verifyResult.vEmail,
    verifyResult.foreverEmail,
    verifyResult.videoEmail,
  ].map(normalizeEmail);
  return ownerEmails.includes(userEmail);
}

/** Fallback ownership check via the user's cami-code lists (verify API often omits owner fields on empty QRs). */
async function camiCodeOwnedInUserList(camiCode, password, profile) {
  if (!profile || !camiCode || !password) {
    return false;
  }

  try {
    const { rows, ownerFilterId } = await fetchOwnedCamiRows(profile);
    const match = rows.find((row) => rowMatchesScannedCami(row, camiCode, password));
    if (!match) {
      return false;
    }
    return rowOwnedByProfile(match, profile, ownerFilterId);
  } catch {
    return false;
  }
}

async function resolveIsOwner(verifyResult, authUser, profile, camiCode, password) {
  if (camiCodeBelongsToUser(verifyResult, authUser, profile)) {
    return true;
  }
  if (profile && (await camiCodeOwnedInUserList(camiCode, password, profile))) {
    return true;
  }
  if (profile && (await camiCodeOwnedViaGetCamiCode(camiCode, password, authUser, profile))) {
    return true;
  }
  return false;
}

export function buildScanReturnLocation(camiCode, password) {
  const params = new URLSearchParams();
  params.set("c", camiCode);
  params.set("p", password);
  return { pathname: "/scan-qr", search: `?${params.toString()}` };
}

const PENDING_QR_RETURN_KEY = "PENDING_QR_RETURN_V1";

export function savePendingQrReturn(from) {
  try {
    if (!from?.pathname) return;
    sessionStorage.setItem(
      PENDING_QR_RETURN_KEY,
      JSON.stringify({
        pathname: from.pathname,
        search: from.search || "",
        hash: from.hash || "",
      })
    );
  } catch {
    /* ignore */
  }
}

export function readPendingQrReturn() {
  try {
    const raw = sessionStorage.getItem(PENDING_QR_RETURN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.pathname) return null;
    return {
      pathname: parsed.pathname,
      search: parsed.search || "",
      hash: parsed.hash || "",
    };
  } catch {
    return null;
  }
}

export function clearPendingQrReturn() {
  try {
    sessionStorage.removeItem(PENDING_QR_RETURN_KEY);
  } catch {
    /* ignore */
  }
}

/** Decide what to do when a QR code has no playable video yet. */
export async function resolveNoVideoAccess({
  verifyResult,
  authUser = null,
  profile = null,
  camiCode,
  password,
  returnLocation = null,
}) {
  const effectiveAuthUser = buildEffectiveAuthUser(authUser, profile);

  if (
    camiCodeBelongsToUser(verifyResult, effectiveAuthUser, profile) ||
    (profile && (await camiCodeOwnedInUserList(camiCode, password, profile))) ||
    (profile && (await camiCodeOwnedViaGetCamiCode(camiCode, password, effectiveAuthUser, profile)))
  ) {
    return {
      action: "upload",
      url: `/upload-video?c=${encodeURIComponent(camiCode)}&p=${encodeURIComponent(password)}`,
    };
  }

  if (!effectiveAuthUser) {
    const from = returnLocation || buildScanReturnLocation(camiCode, password);
    savePendingQrReturn(from);
    return {
      action: "login",
      message:
        "There is no video on this QR code yet. Log in or register if this is your QR code, or scan a different QR code that already has a video.",
      loginState: {
        from,
        noVideoMessage:
          "No video is available for this QR code. If it's yours, please log in or sign up to upload one.",
      },
    };
  }

  return {
    action: "error",
    message:
      "There is no video on this QR code and you do not have permission to upload to it.",
  };
}

/** Parse a scanned QR string or pasted link into cami credentials or encoded token. */
export function parseQrLinkText(text) {
  const trimmed = String(text || "").trim();
  if (!trimmed) {
    return { camiCode: "", password: "", encoded: "" };
  }

  try {
    const parsedUrl = new URL(trimmed);
    const camiCodeFromUrl = (parsedUrl.searchParams.get("c") || "").trim();
    const passwordFromUrl = (parsedUrl.searchParams.get("p") || "").trim();
    if (camiCodeFromUrl && passwordFromUrl) {
      return { camiCode: camiCodeFromUrl, password: passwordFromUrl, encoded: "" };
    }

    const encodedFromQuery = (parsedUrl.searchParams.get("camicodeEncoded") || "").trim();
    if (encodedFromQuery) {
      return { camiCode: "", password: "", encoded: encodedFromQuery };
    }

    const pathSegments = parsedUrl.pathname.split("/").filter(Boolean);
    const lastSegment = (pathSegments[pathSegments.length - 1] || "").trim();
    if (lastSegment && !KNOWN_APP_PATHS.has(lastSegment.toLowerCase())) {
      return { camiCode: "", password: "", encoded: lastSegment };
    }
  } catch {
    if (!trimmed.includes("/") && !trimmed.includes("?")) {
      return { camiCode: "", password: "", encoded: trimmed };
    }
  }

  return { camiCode: "", password: "", encoded: "" };
}

function parseQrFieldsFromPathAndSearch(pathname, search) {
  const params = new URLSearchParams(search || "");
  const camiCode = (params.get("c") || "").trim();
  const password = (params.get("p") || "").trim();
  if (camiCode && password) {
    return { camiCode, password, encoded: "" };
  }

  const encodedFromQuery = (params.get("camicodeEncoded") || "").trim();
  if (encodedFromQuery) {
    return { camiCode: "", password: "", encoded: encodedFromQuery };
  }

  const pathSegments = String(pathname || "")
    .split("/")
    .filter(Boolean);
  const lastSegment = (pathSegments[pathSegments.length - 1] || "").trim();
  if (lastSegment && !KNOWN_APP_PATHS.has(lastSegment.toLowerCase())) {
    return { camiCode: "", password: "", encoded: lastSegment };
  }

  return { camiCode: "", password: "", encoded: "" };
}

function parseHashAsPathAndSearch(hash) {
  const raw = String(hash || "").replace(/^#/, "").trim();
  if (!raw) {
    return { pathname: "", search: "" };
  }
  const withSlash = raw.startsWith("/") ? raw : `/${raw}`;
  const queryIndex = withSlash.indexOf("?");
  if (queryIndex >= 0) {
    return {
      pathname: withSlash.slice(0, queryIndex),
      search: withSlash.slice(queryIndex),
    };
  }
  return { pathname: withSlash, search: "" };
}

function credentialsFromDecodePayload(value) {
  const text = String(value || "").trim().replace(/^\uFEFF/, "");
  if (!text) {
    return { camiCode: "", password: "" };
  }

  const fromParams = (params) => ({
    camiCode: (params.get("c") || "").trim(),
    password: (params.get("p") || "").trim(),
  });

  let parsed = fromParams(new URLSearchParams(text));
  if (parsed.camiCode && parsed.password) {
    return parsed;
  }

  try {
    const asUrl = new URL(text);
    parsed = fromParams(asUrl.searchParams);
    if (parsed.camiCode && parsed.password) {
      return parsed;
    }
  } catch {
    /* not a URL */
  }

  try {
    const json = JSON.parse(text);
    if (json && typeof json === "object") {
      return {
        camiCode: String(json.c || json.camiCode || "").trim(),
        password: String(json.p || json.uniqueId || json.password || "").trim(),
      };
    }
  } catch {
    /* not JSON */
  }

  return { camiCode: "", password: "" };
}

/** Parse the current browser location for cami credentials or encoded token. */
export function parseQrLinkFromLocation(location = window.location) {
  const fromUrl = parseQrFieldsFromPathAndSearch(location.pathname, location.search);
  if ((fromUrl.camiCode && fromUrl.password) || fromUrl.encoded) {
    return fromUrl;
  }

  const hashParts = parseHashAsPathAndSearch(location.hash);
  if (hashParts.pathname || hashParts.search) {
    return parseQrFieldsFromPathAndSearch(hashParts.pathname, hashParts.search);
  }

  return { camiCode: "", password: "", encoded: "" };
}

/**
 * Public /scan-qr location that preserves QR credentials from an external link.
 * Used when a QR URL lands on an auth-gated route such as `/`.
 */
export function getPublicQrScanLocation(location = window.location) {
  const parsed = parseQrLinkFromLocation(location);
  const params = new URLSearchParams(location.search || "");

  if (parsed.camiCode && parsed.password) {
    params.set("c", parsed.camiCode);
    params.set("p", parsed.password);
    return { pathname: "/scan-qr", search: `?${params.toString()}` };
  }

  if (parsed.encoded) {
    params.set("camicodeEncoded", parsed.encoded);
    return { pathname: "/scan-qr", search: `?${params.toString()}` };
  }

  return {
    pathname: "/scan-qr",
    search: location.search || "",
  };
}

export async function decodeCamiCodeEncoded(encoded, getAuthHeaders = () => ({})) {
  const api = new URL("/api/CamiCode/GetCamiCodes_decodeURL", baseURL);
  api.searchParams.set("camicodeEncoded", encoded);
  const response = await fetch(api.toString(), {
    method: "GET",
    mode: "cors",
    cache: "no-cache",
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(errorText || `Decode request failed with status ${response.status}`);
  }
  const value = await response.text();
  return credentialsFromDecodePayload(value);
}

/** Resolve camiCode + password from a link location, decoding encoded tokens when needed. */
export async function resolveCamiCodeFromLink(location, getAuthHeaders = () => ({})) {
  const parsed = parseQrLinkFromLocation(location);
  if (parsed.camiCode && parsed.password) {
    return parsed;
  }
  if (!parsed.encoded) {
    return { camiCode: "", password: "", encoded: "" };
  }

  const decoded = await decodeCamiCodeEncoded(parsed.encoded, getAuthHeaders);
  return {
    camiCode: decoded.camiCode,
    password: decoded.password,
    encoded: parsed.encoded,
  };
}

/**
 * Verify a cami code and return the next action.
 * @returns {Promise<{action: 'watch'|'upload'|'login'|'error'|'owner-options', url?: string, message?: string, loginState?: object}>}
 */
export async function verifyCamiCodeScan({
  camiCode,
  password,
  getAuthHeaders = () => ({}),
  authUser = null,
  profile = null,
  foreverCami = 0,
  redirPath = "/",
  showOwnerOptions = false,
}) {
  if (!camiCode || !password) {
    return { action: "error", message: "Invalid QR code link." };
  }

  const api = new URL("/api/CamiCode/VerifyCamiCode", baseURL);
  api.searchParams.set("camiCode", camiCode);
  api.searchParams.set("uniqueId", password);

  let result;
  try {
    const response = await fetch(api.toString(), {
      method: "GET",
      mode: "cors",
      cache: "no-cache",
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorText = await response.text().catch(() => "");
      throw new Error(errorText || `VerifyCamiCode failed with status ${response.status}`);
    }
    result = await response.json();
    if (!result || typeof result !== "object") {
      throw new Error("Invalid QR code response.");
    }
  } catch (error) {
    return {
      action: "error",
      message: error?.message || "Something went wrong while opening this QR link.",
    };
  }

  const videoDuration = result["duration"];
  const videoId = result["videoId"];
  const vEmail = result["vEmail"];
  const fileUrl = result["fileUrl"];
  const qrVideos = videosFromCamiListRow(result);
  const hasPlayableMedia = qrVideos.length > 0 || !!(videoId || fileUrl);
  const redir = encodeURIComponent(redirPath);
  const latestVideoId = await resolveLatestVideoIdFromCamiRow(result);
  const isOwner = await resolveIsOwner(result, authUser, profile, camiCode, password);
  const codeParams = `c=${encodeURIComponent(camiCode)}&p=${encodeURIComponent(password)}`;
  const watchUrl = buildWatchUrl({
    camiCode,
    uniqueId: password,
    videoId: latestVideoId || videoId || undefined,
    redir,
    email: vEmail || undefined,
  });

  // Owners always get Record / Upload / Watch options (Watch only when media exists).
  // Non-owners with media go straight to watch.
  if (showOwnerOptions && isOwner) {
    applyDurationLocalStorage(videoDuration);
    return {
      action: "owner-options",
      title: result?.title || result?.gameName || camiCode,
      videoCount: qrVideos.length || (videoId || fileUrl ? 1 : 0),
      hasPlayableMedia,
      watchUrl: hasPlayableMedia ? watchUrl : null,
      recordUrl: `/create-qr?${codeParams}`,
      uploadUrl: `/upload-video?${codeParams}`,
    };
  }

  if (hasPlayableMedia) {
    applyDurationLocalStorage(videoDuration);
    return {
      action: "watch",
      url: watchUrl,
    };
  }

  applyDurationLocalStorage(videoDuration);
  return resolveNoVideoAccess({
    verifyResult: result,
    authUser,
    profile,
    camiCode,
    password,
  });
}

export function hasQrLinkParams(location = window.location) {
  const parsed = parseQrLinkFromLocation(location);
  return !!(parsed.camiCode && parsed.password) || !!parsed.encoded;
}
